import { useNavigate } from "react-router-dom";
import { FC, MouseEvent, SyntheticEvent, useEffect, useState } from "react";
import { AddCircleOutline, MoreVert, Search } from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import Moment from "../../../common/Moment/Moment";

import styles from "../ECMembers.module.scss";
import { EC_MEMBER_TYPE, ROLES, RoutingList } from "../../../../constants";
import {
  useGetAllEcMemberLazyQuery,
  useRemoveUserMutation,
  useUpdateEcRoleMutation,
} from "../../../../generated/graphql";
import useAppStore from "../../../../states/zustand/appStore";
import TableFooter from "../../IRB/IRBSubmission/Table/TableFooter/TableFooter";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../common/confirmationModal/ConfirmationModal";
import useDebounce from "../../../../hooks/useDebounce";

const ECMembers: FC = (props): JSX.Element => {
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRole, setSelectedRole] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const navigate = useNavigate();
  const [removeECM] = useRemoveUserMutation();
  const [updateECRole] = useUpdateEcRoleMutation();
  const [getAllEC, { data, loading }] = useGetAllEcMemberLazyQuery();
  const [debouncedValue, value, setValue] = useDebounce<string>("", 1000);

  const inputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e?.target?.value);
  };

  const openConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const deleteHandler = async () => {
    // await handleDelete(item._id);
    handleDelete();
    setShowConfirmationModal(false);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const getAllIrbSub = async () => {
    await getAllEC({
      variables: {
        page: page + 1,
        perPage: rowsPerPage,
        keyword: debouncedValue,
        role: selectedRole,
      },
    });
  };

  const { setLoader } = useAppStore();
  useEffect(() => {
    getAllIrbSub();
  }, [page, rowsPerPage, getAllEC, debouncedValue, selectedRole]);

  useEffect(() => {
    setLoader(loading);
  }, [loading]);

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const rowInputHandler = async (
    e: SyntheticEvent<Element, Event>,
    newValue: any,
    id: any,
    role: any
  ) => {
    await updateECRole({
      variables: {
        _id: id,
        rolesInEC: newValue,
        role: role,
      },
    });
    getAllIrbSub();
  };

  const handleMoreOpen = (event: MouseEvent<HTMLElement>, id: string) => {
    setSelectedId(id);
    setMoreAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  const handleView = () => {
    navigate(RoutingList.members.details.path.replace(":id", selectedId));
  };

  const handleEdit = () => {
    navigate(RoutingList.members.edit.path.replace(":id", selectedId));
  };

  const navigateToCreateECMember = () => {
    navigate(RoutingList.members.create.path);
  };

  const handleDelete = async () => {
    try {
      let res = await removeECM({
        variables: {
          _id: selectedId,
        },
      });
      if (!res.data) throw null;
      getAllEC({
        variables: {
          page: 1,
          perPage: rowsPerPage,
          keyword: debouncedValue,
          role: selectedRole,
        },
      });
      toast.success("Deleted successfully!");
    } catch (error) {
      toast.error("Could not delete!");
    } finally {
      handleMoreClose();
    }
  };

  return (
    <>
      <Container maxWidth={false}>
        <Card>
          <CardContent className="pd-x-0 pd-y-0">
            <Box className="d-flex align-items-center justify-content-between pd-x-32 pd-t-32 pd-b-20">
              <Typography variant="h2" component="h2">
                All Members
              </Typography>
              <OutlinedInput
                id="outlined-adornment-weight"
                name="searchField"
                value={value}
                onChange={inputHandler}
                endAdornment={
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                }
                aria-describedby="outlined-weight-helper-text"
                inputProps={{
                  "aria-label": "weight",
                }}
                className={"inputField"}
              />
              <Autocomplete
                // freeSolo
                disablePortal
                value={selectedRole}
                options={Object.values(ROLES).filter(
                  (role) => role !== ROLES.ECS && role !== ROLES.INVESTIGATOR
                )}
                className={"inputField"}
                sx={{ width: "250px" }}
                onChange={(e, newValue) =>
                  // rowInputHandler(e, newValue, item._id, item.role)
                  setSelectedRole(newValue || "")
                }
                renderInput={(params) => <TextField sx={{height: '40px'}} {...params} />}
              />
              <Button
                size={"small"}
                variant={"contained"}
                color={"error"}
                onClick={navigateToCreateECMember}
                type="button"
              >
                <AddCircleOutline
                  className="mg-r-10"
                  sx={{ width: ".875rem", height: ".875rem" }}
                />
                Add Member
              </Button>
            </Box>
            <TableContainer className="custom-table">
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell className="pd-l-32">Member details</TableCell>
                    <TableCell>Qualification</TableCell>
                    <TableCell>Appointment</TableCell>
                    <TableCell>Committee Type</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell className="text-center">
                      Affiliation to Medanta
                    </TableCell>
                    <TableCell className="pd-r-32"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.getAllUser?.users?.map((item, i: number) => (
                    <TableRow
                      key={i}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell className="pd-l-32">
                        <Box className="d-flex align-items-center">
                          <Avatar
                            className="mg-r-20"
                            alt={`${item.name}`}
                            src={`${item.avatar}`}
                          />
                          <div>
                            <Typography
                              variant={"subtitle1"}
                              className="font-weight-semi-bold"
                            >
                              {item.name}
                            </Typography>
                            <Typography
                              className="font-weight-semi-bold font-size-xs"
                              color={"secondary"}
                            >
                              Updated <Moment date={item.updatedAt} />
                            </Typography>
                          </div>
                        </Box>
                      </TableCell>
                      <TableCell>{item.qualification.join(", ")}</TableCell>
                      <TableCell>
                        <Moment date={item.dateOfAppointment} />
                      </TableCell>
                      <TableCell>{item.role}</TableCell>
                      <TableCell>
                        {/* <Autocomplete
                          // freeSolo
                          disablePortal
                          value={item.rolesInEC}
                          options={EC_MEMBER_TYPE}
                          onChange={(e, newValue) =>
                            rowInputHandler(e, newValue, item._id, item.role)
                          }
                          renderInput={(params) => <TextField {...params} />}
                        /> */}
                        {item.rolesInEC}
                      </TableCell>
                      <TableCell className="text-center">
                        {true ? (
                          <Chip
                            size={"small"}
                            className={`${styles.chips} ${styles.textWhite}`}
                            label="Yes"
                            color={"success"}
                          />
                        ) : (
                          <Chip
                            size={"small"}
                            className={`${styles.chips} ${styles.bgRed}`}
                            label="No"
                            color={"error"}
                          />
                        )}
                      </TableCell>
                      <TableCell className="pd-r-32">
                        <IconButton
                          onClick={(e) => handleMoreOpen(e, item._id || "")}
                          sx={{ p: 0 }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableFooter
              count={data?.getAllUser.totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
        <Menu
          className={styles.menu}
          id="more-menu"
          anchorEl={moreAnchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(moreAnchorEl)}
          onClose={handleMoreClose}
        >
          <MenuItem onClick={handleEdit}>
            <Typography textAlign="center">Edit</Typography>
          </MenuItem>
          <MenuItem onClick={handleView}>
            <Typography textAlign="center">View</Typography>
          </MenuItem>
          <MenuItem onClick={openConfirmationModal}>
            <Typography textAlign="center">Delete</Typography>
          </MenuItem>
        </Menu>
      </Container>
      <ConfirmationModal
        submitText={"Delete"}
        open={showConfirmationModal}
        onConfirm={deleteHandler}
        onClose={closeConfirmationModal}
        description="Are you sure to delete this member? You can not revert the changes."
      />
    </>
  );
};

export default ECMembers;
